import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, ShieldAlert, FileUp, CheckCircle2, XCircle, AlertCircle, AlertTriangle,
  FileSearch, Ruler, FileMinus, FilePlus, RefreshCw, Info, ChevronDown, ChevronUp, Download
} from "lucide-react";
import Alert from "../../components/ui/Alert.tsx";
import Breadcrumbs from "../../components/ui/Breadcrumbs.tsx";
import XmlDropzone from "../../components/shared/XmlDropzone.tsx";
import CopyIconButton from "../../components/shared/CopyIconButton.tsx";
import {
  parseXmlString, xmlToPathMap, compararEstructuras, ResultadoComparacionEstructura,
  formatearPathVisual, truncarValor, construirEntradas, agruparEntradas,
  DiscrepanciaEntry, GrupoDiscrepancias, GRUPO_GENERAL_KEY, SeveridadDiscrepancia,
  detectarPrefijoComun, resumirEntradas, construirReporteTexto, BADGE_RESTRICCION,
  validarRestricciones, agruparPorTag, ResultadoRestriccion, GrupoRestriccion
} from "../../utils/xmlStructureUtils.ts";
import {
  listarTiposDocumento,
  resolverPlantilla,
  PlantillaResuelta,
  TiposDocumentoSeparados,
  TipoDocumentoResumen
} from "../../services/plantillaReferenciaService.ts";

const TIPOS_VACIOS: TiposDocumentoSeparados = { globales: [], proyecto: [] };

// Label enriquecido de cada opción del dropdown: "{nombre} · {documentType} ·
// {documentStage} · {documentStandard}", omitiendo los campos vacíos — si no
// hay ninguno configurado, queda igual que antes (solo el nombre). `nombre`
// (etiqueta legible) es el label principal, con fallback a `tipoDocumento`
// (el id de Firestore, con guiones y sufijo de stage) para plantillas de
// antes de que existiera el campo nombre — mismo criterio que
// ValidatorTemplatesSection.tsx. Una sola línea porque <option> nativo no
// soporta multilínea/sublabels; el <select> nativo se mantiene (accesible,
// sin componente nuevo) en vez de un dropdown custom de dos líneas.
const construirLabelTipo = (t: TipoDocumentoResumen): string => {
  const nombrePrincipal = t.nombre || t.tipoDocumento;
  const partes = [t.documentType, t.documentStage, t.documentStandard].filter((v) => !!v && v.trim());
  return partes.length > 0 ? `${nombrePrincipal} · ${partes.join(" · ")}` : nombrePrincipal;
};

interface ValidadorEstructuraProps {
  // Presente = el validador vive dentro de un proyecto puntual: además de
  // las plantillas globales, también se pueden usar las propias del
  // proyecto (con prioridad). La gestión de esas plantillas (cargar/
  // renombrar/eliminar) vive en Configuración > Plantillas XML — acá solo
  // se seleccionan y se usan.
  projectId?: string;
  projectNombre?: string;
  setPage?: (page: string) => void;
}

export default function ValidadorEstructura({ projectId, projectNombre, setPage }: ValidadorEstructuraProps) {
  const [tipos, setTipos] = useState<TiposDocumentoSeparados>(TIPOS_VACIOS);
  const [tipoDocumento, setTipoDocumento] = useState("");

  const [plantilla, setPlantilla] = useState<PlantillaResuelta | null>(null);
  const [cargandoPlantilla, setCargandoPlantilla] = useState(false);

  const [nombreDocumento, setNombreDocumento] = useState("");
  const [contenidoDocumento, setContenidoDocumento] = useState("");

  const [resultado, setResultado] = useState<ResultadoComparacionEstructura | null>(null);
  const [validando, setValidando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");

  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 5000);
  };

  useEffect(() => {
    listarTiposDocumento(projectId)
      .then(setTipos)
      .catch((err) => console.error("Error listando tipos de documento:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    // Cambiar de tipo invalida cualquier archivo que ya se hubiera subido
    // para el tipo anterior — evita confundir un XML de referencia con el
    // documento a validar (o viceversa) al saltar entre tipos.
    setNombreDocumento("");
    setContenidoDocumento("");
    setResultado(null);

    if (!tipoDocumento) { setPlantilla(null); return; }
    setCargandoPlantilla(true);
    resolverPlantilla(tipoDocumento, projectId)
      .then(setPlantilla)
      .catch((err) => {
        console.error("Error resolviendo plantilla:", err);
        showAlert("No se pudo buscar la plantilla de referencia.", "error");
      })
      .finally(() => setCargandoPlantilla(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoDocumento, projectId]);

  const handleArchivoCargado = (nombre: string, texto: string) => {
    setNombreDocumento(nombre);
    setContenidoDocumento(texto);
    setResultado(null);
  };

  const handleValidar = () => {
    if (!plantilla?.plantilla?.contenidoXML || !contenidoDocumento) return;

    setValidando(true);
    try {
      let mapaPlantilla: Record<string, string>;
      try {
        mapaPlantilla = xmlToPathMap(parseXmlString(plantilla.plantilla.contenidoXML));
      } catch (err: any) {
        throw new Error(`La plantilla guardada no es un XML válido (${err.message || err}). Cárgala de nuevo desde Configuración > Plantillas XML.`);
      }

      let mapaDocumento: Record<string, string>;
      try {
        mapaDocumento = xmlToPathMap(parseXmlString(contenidoDocumento));
      } catch (err: any) {
        throw new Error(`El documento subido no es un XML válido (${err.message || err}).`);
      }

      setResultado(compararEstructuras(mapaPlantilla, mapaDocumento));
    } catch (err: any) {
      showAlert(err.message || "Error al procesar los XML.", "error");
      setResultado(null);
    } finally {
      setValidando(false);
    }
  };

  // Entradas ya reclasificadas (ver construirEntradas en xmlStructureUtils.ts)
  // — cruza longitudExcedida contra las restricciones configuradas en la
  // plantilla para decidir si cada una es error o advertencia.
  const entradas = useMemo(
    () => (resultado ? construirEntradas(resultado, plantilla?.plantilla?.restricciones || []) : []),
    [resultado, plantilla]
  );
  const resumen = useMemo(() => resumirEntradas(entradas), [entradas]);

  // "Estructura OK" solo depende de errores y faltantes — las advertencias
  // (diferencias sin regla de negocio configurada) y los campos nuevos
  // (informativos) ya no se tratan como problemas bloqueantes.
  const todoOK = resultado ? resumen.errores === 0 && resumen.faltantes === 0 : false;

  // Prefijo raíz común a todos los paths del resultado — se muestra una
  // sola vez como contexto en el header, en vez de repetirlo en cada fila.
  const prefijoComun = useMemo(() => detectarPrefijoComun(entradas.map((e) => e.path)), [entradas]);

  // Agrupado por ítem (ver xmlStructureUtils.ts) para el Panel de
  // Discrepancias — "General" (campos fuera de cualquier array) siempre
  // abierto; los grupos "Item N" arrancan abiertos solo si son 3 o menos.
  const grupos = useMemo(() => agruparEntradas(entradas), [entradas]);
  const cantidadGruposItem = useMemo(() => grupos.filter((g) => g.key !== GRUPO_GENERAL_KEY).length, [grupos]);

  // Panel de Restricciones: independiente del Panel de Discrepancias de
  // arriba (ver comentario en el render) — corre en el mismo momento
  // (después de "Validar") sobre las restricciones técnicas de la
  // plantilla resuelta, si las tiene.
  const hayRestriccionesConfiguradas = (plantilla?.plantilla?.restricciones?.length || 0) > 0;
  const resultadosRestricciones = useMemo(() => {
    const restricciones = plantilla?.plantilla?.restricciones;
    if (!resultado || !restricciones || restricciones.length === 0) return [];
    return validarRestricciones(contenidoDocumento, restricciones);
  }, [resultado, contenidoDocumento, plantilla]);
  const gruposRestricciones = useMemo(() => agruparPorTag(resultadosRestricciones), [resultadosRestricciones]);
  const sinViolacionesRestricciones = resultadosRestricciones.length === 0;

  const handleDescargarReporte = () => {
    const texto = construirReporteTexto(
      entradas,
      resultadosRestricciones,
      { tipo: tipoDocumento, nombreArchivo: plantilla?.plantilla?.nombreArchivo, alcance: plantilla?.alcance },
      nombreDocumento
    );
    const blob = new Blob([texto], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const fechaArchivo = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `validacion_${tipoDocumento || "documento"}_${fechaArchivo}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1 ml-1";

  return (
    <main className={setPage ? "min-h-screen p-4 md:p-8 text-white" : "text-white"}>
      {setPage && (
        <div className="max-w-7xl mx-auto mb-4">
          <Breadcrumbs currentPage="validador-estructura" setPage={setPage} />
        </div>
      )}

      {setPage && (
        <header className="max-w-7xl mx-auto mb-10 text-center">
          <h1 className="text-4xl font-black bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-600 tracking-tighter">
            ONEHUB <span className="text-white/10 font-thin">|</span> VALIDADOR DE ESTRUCTURA
          </h1>
          <p className="text-gray-500 mt-2 font-medium">Compara un documento contra una plantilla de referencia que ya funcionó</p>
        </header>
      )}

      <div className="max-w-7xl mx-auto space-y-8">
        <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

        {/* SELECTOR DE TIPO DE DOCUMENTO */}
        <section className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
          <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4 text-indigo-400">
            <FileSearch size={24} /> <h2 className="font-bold uppercase tracking-tighter">Tipo de documento</h2>
          </div>

          <div>
            <label className={labelClass}>Tipo</label>
            <select
              className={inputClass}
              value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.target.value)}
            >
              <option value="">Selecciona un tipo</option>
              {tipos.globales.length > 0 && (
                <optgroup label="Plantillas globales">
                  {tipos.globales.map((t) => (
                    <option key={`g-${t.tipoDocumento}`} value={t.tipoDocumento} title={t.descripcion || undefined}>
                      {construirLabelTipo(t)}
                    </option>
                  ))}
                </optgroup>
              )}
              {projectId && tipos.proyecto.length > 0 && (
                <optgroup label={projectNombre || "Este proyecto"}>
                  {tipos.proyecto.map((t) => (
                    <option key={`p-${t.tipoDocumento}`} value={t.tipoDocumento} title={t.descripcion || undefined}>
                      {construirLabelTipo(t)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {tipoDocumento && (
            <div className="mt-6">
              {cargandoPlantilla ? (
                <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Buscando plantilla...</p>
              ) : plantilla?.encontrada ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${
                    plantilla.alcance === "proyecto" ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30" : "bg-white/10 text-gray-300 border border-white/20"
                  }`}>
                    <ShieldCheck size={14} />
                    {plantilla.alcance === "proyecto" ? "Usando plantilla del proyecto" : "Usando plantilla global"}
                    {plantilla.plantilla?.nombreArchivo && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="normal-case font-medium tracking-normal">{plantilla.plantilla.nombreArchivo}</span>
                      </>
                    )}
                  </span>
                </div>
              ) : (
                <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  <AlertCircle size={14} />
                  Sin plantilla de referencia para "{tipoDocumento}"
                </span>
              )}
            </div>
          )}
        </section>

        {/* DOCUMENTO A VALIDAR (si hay plantilla resuelta) */}
        {tipoDocumento && !cargandoPlantilla && (
          plantilla?.encontrada ? (
            <section className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
              <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4 text-purple-400">
                <FileUp size={24} /> <h2 className="font-bold uppercase tracking-tighter">Documento a validar</h2>
              </div>

              <XmlDropzone
                accent="purple"
                nombreArchivo={nombreDocumento}
                contenidoArchivo={contenidoDocumento}
                onArchivoCargado={handleArchivoCargado}
                onError={(msg) => showAlert(msg, "error")}
              />

              <button
                onClick={handleValidar}
                disabled={!contenidoDocumento || validando}
                className="w-full mt-8 py-5 rounded-2xl font-black text-sm uppercase tracking-tighter transition-all flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:scale-[1.01] shadow-xl shadow-indigo-900/20 disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                {validando ? <RefreshCw size={20} className="animate-spin" /> : <ShieldCheck size={20} />}
                Validar
              </button>
            </section>
          ) : (
            <section className="bg-amber-500/[0.03] backdrop-blur-2xl border border-amber-500/20 p-8 rounded-[2.5rem] shadow-2xl">
              <div className="flex items-center gap-3 mb-3 text-amber-400">
                <AlertCircle size={24} />
                <h2 className="font-bold uppercase tracking-tighter">Sin plantilla para "{tipoDocumento}"</h2>
              </div>
              <p className="text-xs text-gray-400">
                {projectId
                  ? <>Todavía no existe una plantilla de referencia para este tipo, ni global ni de este proyecto. Cárgala desde <span className="text-gray-300 font-medium">Configuración &gt; Plantillas XML</span>, o pídele a un administrador que agregue una global desde el panel Admin.</>
                  : <>Todavía no existe una plantilla de referencia global para este tipo de documento. Entra a un proyecto para cargar una propia desde Configuración, o pídele a un administrador que la agregue desde el panel Admin.</>}
              </p>
            </section>
          )
        )}

        {/* PANEL DE DISCREPANCIAS */}
        {resultado && (
          <section className="bg-[rgb(var(--c-bg))]/80 backdrop-blur-md border border-white/10 rounded-[3rem] p-10 shadow-3xl animate-slideUp">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h2 className="text-2xl font-black flex items-center gap-3">
                <ShieldCheck className="text-emerald-400" size={32} /> PANEL DE DISCREPANCIAS
              </h2>
              <div className={`px-4 py-1 rounded-full text-[10px] font-black uppercase ${todoOK ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {todoOK ? "Estructura OK" : "Diferencias encontradas"}
              </div>
            </div>

            {prefijoComun && (
              <p className="flex items-center gap-1.5 text-xs text-indigo-300 mb-3 font-mono">
                <Info size={12} className="flex-shrink-0" />
                Contexto: {formatearPathVisual(prefijoComun)}
              </p>
            )}

            <div className="space-y-1.5 mb-6">
              <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <Info size={12} className="flex-shrink-0" />
                Los índices corresponden a la posición en el XML (comenzando en 1).
              </p>
              {hayRestriccionesConfiguradas && (
                <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <Info size={12} className="flex-shrink-0" />
                  Una diferencia acá no es necesariamente un error técnico — revisa el Panel de Restricciones más abajo para las reglas de negocio configuradas en esta plantilla.
                </p>
              )}
            </div>

            {/* RESUMEN EJECUTIVO */}
            <div className="mb-8 text-sm font-bold">
              {todoOK ? (
                <span className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 size={18} /> Sin diferencias encontradas ✓
                </span>
              ) : (
                <span>
                  <span className="text-red-400">{resumen.errores} error{resumen.errores !== 1 ? "es" : ""}</span>
                  <span className="text-gray-600 mx-2">·</span>
                  <span className="text-red-400">{resumen.faltantes} faltante{resumen.faltantes !== 1 ? "s" : ""}</span>
                  <span className="text-gray-600 mx-2">·</span>
                  <span className="text-amber-400">{resumen.advertencias} advertencia{resumen.advertencias !== 1 ? "s" : ""}</span>
                  <span className="text-gray-600 mx-2">·</span>
                  <span className="text-sky-400">{resumen.camposNuevos} campo{resumen.camposNuevos !== 1 ? "s" : ""} nuevo{resumen.camposNuevos !== 1 ? "s" : ""}</span>
                </span>
              )}
            </div>

            {(resumen.advertencias > 0 || resumen.camposNuevos > 0) && (
              <div className="space-y-1 -mt-4 mb-6">
                {resumen.advertencias > 0 && (
                  <p className="flex items-start gap-1.5 text-[11px] text-amber-400/80">
                    <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                    Los campos marcados como Diferencia cambiaron de longitud pero no violan ninguna restricción configurada.
                  </p>
                )}
                {resumen.camposNuevos > 0 && (
                  <p className="flex items-start gap-1.5 text-[11px] text-sky-400/80">
                    <Info size={12} className="flex-shrink-0 mt-0.5" />
                    Los campos marcados como Campo nuevo no estaban en la plantilla. Pueden ser opcionales.
                  </p>
                )}
              </div>
            )}

            {grupos.map((grupo) => (
              <GrupoDiscrepanciasCard
                key={grupo.key}
                grupo={grupo}
                abiertoInicial={grupo.key === GRUPO_GENERAL_KEY || cantidadGruposItem <= 3}
                prefijoComun={prefijoComun}
              />
            ))}
          </section>
        )}

        {/* PANEL DE RESTRICCIONES — independiente del Panel de Discrepancias
            de arriba: ese compara VALORES contra la plantilla, este valida
            reglas técnicas configuradas (longitud, solo numérico, valores
            permitidos, requerido) sin importar si el valor coincide con la
            plantilla. Un campo puede aparecer en uno, el otro, ambos o
            ninguno — son chequeos independientes. Solo se muestra si la
            plantilla resuelta tiene restricciones configuradas. */}
        {resultado && hayRestriccionesConfiguradas && (
          <section className="bg-[rgb(var(--c-bg))]/80 backdrop-blur-md border border-white/10 rounded-[3rem] p-10 shadow-3xl animate-slideUp">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h2 className="text-2xl font-black flex items-center gap-3">
                <ShieldAlert className="text-red-400" size={32} /> PANEL DE RESTRICCIONES
              </h2>
              <div className={`px-4 py-1 rounded-full text-[10px] font-black uppercase ${sinViolacionesRestricciones ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {sinViolacionesRestricciones ? "Cumple restricciones" : "Restricciones violadas"}
              </div>
            </div>

            <p className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-6">
              <Info size={12} className="flex-shrink-0" />
              Reglas técnicas de esta plantilla, independientes de si el valor coincide con la plantilla — ver Panel de Discrepancias arriba para eso.
            </p>

            <div className="mb-8 text-sm font-bold">
              {sinViolacionesRestricciones ? (
                <span className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 size={18} /> Todos los campos cumplen las restricciones configuradas ✓
                </span>
              ) : (
                <span className="text-red-400">
                  {resultadosRestricciones.length} restricci{resultadosRestricciones.length !== 1 ? "ones" : "ón"} violada{resultadosRestricciones.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {gruposRestricciones.map((grupo) => (
              <GrupoRestriccionesCard
                key={grupo.tag}
                grupo={grupo}
                abiertoInicial={gruposRestricciones.length <= 3}
              />
            ))}
          </section>
        )}

        {resultado && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleDescargarReporte}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
            >
              <Download size={16} /> Descargar reporte
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

// ── Panel de discrepancias: grupo colapsable por ítem ──────────────────────

const ICONO_POR_CATEGORIA: Record<DiscrepanciaEntry["categoria"], React.ElementType> = {
  longitud: Ruler,
  faltante: FileMinus,
  nuevo: FilePlus
};

// Colores/ícono por severidad — reemplaza el antiguo esquema binario
// error/advertencia (ver construirEntradas en xmlStructureUtils.ts sobre
// por qué ahora hay 3 severidades: error, advertencia e info).
const ESTILO_SEVERIDAD: Record<SeveridadDiscrepancia, {
  fila: string;
  textoIcono: string;
  badge: string;
  Icono: React.ElementType;
  colorIcono: string;
}> = {
  error: {
    fila: "bg-red-500/5 border-red-500/10 hover:border-red-500/30",
    textoIcono: "text-red-400",
    badge: "text-red-300 bg-red-500/10 border-red-500/30",
    Icono: XCircle,
    colorIcono: "text-red-500"
  },
  advertencia: {
    fila: "bg-amber-500/5 border-amber-500/10 hover:border-amber-500/30",
    textoIcono: "text-amber-400",
    badge: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    Icono: AlertTriangle,
    colorIcono: "text-amber-500"
  },
  info: {
    fila: "bg-sky-500/5 border-sky-500/10 hover:border-sky-500/30",
    textoIcono: "text-sky-400",
    badge: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    Icono: Info,
    colorIcono: "text-sky-500"
  }
};

function GrupoDiscrepanciasCard({ grupo, abiertoInicial, prefijoComun }: { grupo: GrupoDiscrepancias; abiertoInicial: boolean; prefijoComun: string }) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const cantidad = grupo.entries.length;

  return (
    <div className="mb-4 last:mb-0 rounded-2xl border border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 bg-white/5 hover:bg-white/10 transition-colors text-left"
      >
        <span className="text-sm font-bold text-white">
          {grupo.label} — {cantidad} diferencia{cantidad !== 1 ? "s" : ""}
        </span>
        {abierto ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>

      {abierto && (
        <div className="p-4 border-t border-white/5 space-y-2">
          {grupo.entries.map((entry) => (
            <FilaDiscrepancia key={`${entry.categoria}-${entry.path}`} entry={entry} prefijoComun={prefijoComun} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilaDiscrepancia({ entry, prefijoComun }: { entry: DiscrepanciaEntry; prefijoComun: string }) {
  const estilo = ESTILO_SEVERIDAD[entry.severidad];
  const IconoCategoria = ICONO_POR_CATEGORIA[entry.categoria];
  const pathCompleto = formatearPathVisual(entry.path);
  const pathSinPrefijo = prefijoComun && entry.path.startsWith(`${prefijoComun}/`)
    ? entry.path.slice(prefijoComun.length + 1)
    : entry.path;
  const pathMostrado = formatearPathVisual(pathSinPrefijo);

  return (
    <div className={`flex items-start gap-3 p-4 rounded-2xl border transition-all ${estilo.fila}`}>
      <IconoCategoria size={16} className={`flex-shrink-0 mt-0.5 ${estilo.textoIcono}`} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-mono text-gray-300 truncate" title={pathCompleto}>{pathMostrado}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border flex-shrink-0 ${estilo.badge}`}>
            {entry.badge}
          </span>
        </div>
        <DetalleValores entry={entry} />
        {entry.helperText && (
          <p className="text-[11px] text-gray-500 mt-1 italic">{entry.helperText}</p>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <CopyIconButton value={pathCompleto} title="Copiar path" />
        <estilo.Icono className={`${estilo.colorIcono} w-5 h-5`} />
      </div>
    </div>
  );
}

function ValorEntreComillas({ valor }: { valor: string }) {
  return <span title={valor}>"{truncarValor(valor)}"</span>;
}

// Longitud excedida: valor de plantilla → valor del documento (con largo de
// cada uno). Faltante: solo el valor que tenía la plantilla. Nuevo: solo el
// valor que trae el documento — no hay "plantilla" con la que comparar.
function DetalleValores({ entry }: { entry: DiscrepanciaEntry }) {
  const claseValor = "text-xs font-mono text-gray-400";

  if (entry.categoria === "longitud") {
    return (
      <p className={claseValor}>
        plantilla: <ValorEntreComillas valor={entry.valorPlantilla || ""} /> ({entry.largoPlantilla})
        <span className="text-gray-600 mx-1.5">→</span>
        nuevo: <ValorEntreComillas valor={entry.valorDocumento || ""} /> ({entry.largoDocumento})
      </p>
    );
  }

  if (entry.categoria === "faltante") {
    return <p className={claseValor}>plantilla: <ValorEntreComillas valor={entry.valorPlantilla || ""} /></p>;
  }

  return <p className={claseValor}>nuevo: <ValorEntreComillas valor={entry.valorDocumento || ""} /></p>;
}

// ── Panel de restricciones: grupo colapsable por tag ───────────────────────
// Mismo lenguaje visual que el Panel de Discrepancias (GrupoDiscrepanciasCard/
// FilaDiscrepancia arriba) pero agrupado por tag en vez de por índice de
// array — son ejes de agrupación distintos (ver agruparPorTag), así que es
// un componente hermano, no una reutilización directa. BADGE_RESTRICCION
// vive en xmlStructureUtils.ts porque el reporte descargable también la usa.

function GrupoRestriccionesCard({ grupo, abiertoInicial }: { grupo: GrupoRestriccion; abiertoInicial: boolean }) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const cantidad = grupo.entries.length;

  return (
    <div className="mb-4 last:mb-0 rounded-2xl border border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 bg-white/5 hover:bg-white/10 transition-colors text-left"
      >
        <span className="text-sm font-bold text-white font-mono">
          {grupo.tag} — {cantidad} violaci{cantidad !== 1 ? "ones" : "ón"}
        </span>
        {abierto ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>

      {abierto && (
        <div className="p-4 border-t border-white/5 space-y-2">
          {grupo.entries.map((entry, idx) => (
            <FilaRestriccion key={`${entry.tagConIndice}-${entry.restriccionViolada}-${idx}`} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilaRestriccion({ entry }: { entry: ResultadoRestriccion }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl border bg-red-500/5 border-red-500/10 hover:border-red-500/30 transition-all">
      <XCircle size={16} className="flex-shrink-0 mt-0.5 text-red-400" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-mono text-gray-300 truncate" title={entry.tagConIndice}>{entry.tagConIndice}</span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-red-300 bg-red-500/10 border-red-500/30 flex-shrink-0">
            {BADGE_RESTRICCION[entry.restriccionViolada]}
          </span>
        </div>
        <p className="text-xs font-mono text-gray-400">valor: <ValorEntreComillas valor={entry.valorActual} /></p>
        <p className="text-xs text-gray-400 mt-1">{entry.detalle}</p>
        {entry.descripcionAdmin && (
          <p className="text-[11px] text-gray-500 mt-1 italic">{entry.descripcionAdmin}</p>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <CopyIconButton value={entry.tagConIndice} title="Copiar path" />
        <XCircle className="text-red-500 w-5 h-5" />
      </div>
    </div>
  );
}
